import { Info } from "lucide-react";
import { useState } from "react";
import type { ForecastScheduleResponseApi } from "@/api/models/systemSettings";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";

export interface ForecastRefreshSchedule {
	enabled: boolean;
	daysOfWeek: number[]; // 0 = Sunday, 1 = Monday, etc.
	startHour: number;
	endHour: number;
	interval: "daily" | "every-6-hours" | "every-12-hours";
}

export interface CronExpressionData {
	enabled: boolean;
	cronExpression: string | null;
	timezone: string | null;
	betweenTimeStart: string | null;
	betweenTimeEnd: string | null;
}

export function generateCronExpression(
	schedule: ForecastRefreshSchedule,
): CronExpressionData {
	if (!schedule.enabled) {
		return {
			enabled: false,
			cronExpression: null,
			timezone: null,
			betweenTimeStart: null,
			betweenTimeEnd: null,
		};
	}

	const daysString =
		schedule.daysOfWeek.length === 0 ? "*" : schedule.daysOfWeek.join(",");

	let cronExpression: string;
	switch (schedule.interval) {
		case "daily":
			cronExpression = `0 ${schedule.startHour} * * ${daysString}`;
			break;
		case "every-6-hours":
			cronExpression = `0 */6 * * ${daysString}`;
			break;
		case "every-12-hours":
			cronExpression = `0 */12 * * ${daysString}`;
			break;
		default:
			cronExpression = `0 ${schedule.startHour} * * ${daysString}`;
	}

	const formatTimeOnly = (hour: number): string => {
		return `${hour.toString().padStart(2, "0")}:00:00`;
	};

	return {
		enabled: true,
		cronExpression,
		timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		betweenTimeStart: formatTimeOnly(schedule.startHour),
		betweenTimeEnd: formatTimeOnly(schedule.endHour),
	};
}

export function parseScheduleFromSettings(
	forecastSchedule: ForecastScheduleResponseApi,
): ForecastRefreshSchedule {
	const defaultSchedule: ForecastRefreshSchedule = {
		enabled: forecastSchedule.enabled,
		daysOfWeek: [1, 2, 3, 4, 5],
		startHour: 6,
		endHour: 18,
		interval: "daily",
	};

	if (!forecastSchedule.enabled || !forecastSchedule.cronExpression) {
		return { ...defaultSchedule, enabled: false };
	}

	const cronParts = forecastSchedule.cronExpression.split(" ");
	if (cronParts.length >= 5) {
		const daysPart = cronParts[4];
		if (daysPart !== "*") {
			defaultSchedule.daysOfWeek = daysPart
				.split(",")
				.map((d) => parseInt(d, 10));
		}

		const hourPart = cronParts[1];
		// 'hourly' (*/1 or *) is no longer a supported option — treat as every-6-hours
		if (hourPart === "*" || hourPart === "*/1") {
			defaultSchedule.interval = "every-6-hours";
		} else if (hourPart === "*/6") {
			defaultSchedule.interval = "every-6-hours";
		} else if (hourPart === "*/12") {
			defaultSchedule.interval = "every-12-hours";
		} else {
			defaultSchedule.interval = "daily";
		}
	}

	if (forecastSchedule.betweenTimeStart) {
		const startParts = forecastSchedule.betweenTimeStart.split(":");
		if (startParts.length >= 1) {
			defaultSchedule.startHour = parseInt(startParts[0], 10);
		}
	}

	if (forecastSchedule.betweenTimeEnd) {
		const endParts = forecastSchedule.betweenTimeEnd.split(":");
		if (endParts.length >= 1) {
			defaultSchedule.endHour = parseInt(endParts[0], 10);
		}
	}

	return defaultSchedule;
}

// Compute a human-readable summary of the combined schedule behaviour
function describeSchedule(schedule: ForecastRefreshSchedule): string {
	const { interval, startHour, endHour } = schedule;

	const startLabel = `${startHour.toString().padStart(2, "0")}:00`;
	const endLabel = `${endHour.toString().padStart(2, "0")}:00`;

	if (interval === "daily") {
		return `Once per day at ${startLabel}`;
	}

	const windowHours = endHour > startHour ? endHour - startHour : 0;
	let intervalHours = 6;
	if (interval === "every-12-hours") intervalHours = 12;

	const runsPerDay =
		windowHours > 0 ? Math.floor(windowHours / intervalHours) + 1 : 1;

	const intervalLabel =
		interval === "every-6-hours" ? "Every 6 hours" : "Every 12 hours";

	return `${intervalLabel} between ${startLabel}–${endLabel} = ${runsPerDay} run${runsPerDay !== 1 ? "s" : ""}/day`;
}

interface ForecastRefreshScheduleModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	schedule: ForecastRefreshSchedule;
	onSave: (schedule: ForecastRefreshSchedule) => void;
}

const DAYS = [
	{ label: "Monday", value: 1 },
	{ label: "Tuesday", value: 2 },
	{ label: "Wednesday", value: 3 },
	{ label: "Thursday", value: 4 },
	{ label: "Friday", value: 5 },
	{ label: "Saturday", value: 6 },
	{ label: "Sunday", value: 0 },
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
	label: `${i.toString().padStart(2, "0")}:00`,
	value: i,
}));

export function ForecastRefreshScheduleModal({
	open,
	onOpenChange,
	schedule,
	onSave,
}: ForecastRefreshScheduleModalProps) {
	const [localSchedule, setLocalSchedule] =
		useState<ForecastRefreshSchedule>(schedule);
	const [timeError, setTimeError] = useState<string | null>(null);

	const validateTimeWindow = (start: number, end: number): boolean => {
		if (start > end) {
			setTimeError("Start hour cannot be after end hour");
			return false;
		}
		setTimeError(null);
		return true;
	};

	const handleStartHourChange = (value: string) => {
		const newStart = parseInt(value, 10);
		setLocalSchedule({ ...localSchedule, startHour: newStart });
		validateTimeWindow(newStart, localSchedule.endHour);
	};

	const handleEndHourChange = (value: string) => {
		const newEnd = parseInt(value, 10);
		setLocalSchedule({ ...localSchedule, endHour: newEnd });
		validateTimeWindow(localSchedule.startHour, newEnd);
	};

	const handleDayToggle = (day: number) => {
		const newDays = localSchedule.daysOfWeek.includes(day)
			? localSchedule.daysOfWeek.filter((d) => d !== day)
			: [...localSchedule.daysOfWeek, day].sort();

		setLocalSchedule({ ...localSchedule, daysOfWeek: newDays });
	};

	const handleSave = () => {
		if (!validateTimeWindow(localSchedule.startHour, localSchedule.endHour)) {
			return;
		}

		const scheduleToSave: ForecastRefreshSchedule = {
			...localSchedule,
			enabled: true,
		};

		onSave(scheduleToSave);
		onOpenChange(false);
	};

	const scheduleSummary = describeSchedule(localSchedule);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-2xl">
				<DialogHeader>
					<DialogTitle>Configure Forecast Refresh Schedule</DialogTitle>
					<DialogDescription>
						Set when the Forecast Builder should recalculate predictions and
						recommendations
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-6 py-4">
					{/* Help Text */}
					<div className="flex gap-3 rounded-lg bg-blue-50 p-4 text-sm text-blue-900">
						<Info className="h-5 w-5 flex-shrink-0 text-blue-600" />
						<p>
							The Forecast Builder recalculates all predictions, scenarios, and
							recommendations. This is separate from data source syncs.
						</p>
					</div>

					{/* Days of Week */}
					<div className="space-y-3">
						<Label>Days of Week</Label>
						<div className="flex flex-wrap gap-2">
							{DAYS.map((day) => (
								<Button
									key={day.value}
									type="button"
									variant={
										localSchedule.daysOfWeek.includes(day.value)
											? "default"
											: "outline"
									}
									size="sm"
									onClick={() => handleDayToggle(day.value)}
									className={
										localSchedule.daysOfWeek.includes(day.value)
											? "bg-teal-600 hover:bg-teal-700"
											: ""
									}
								>
									{day.label}
								</Button>
							))}
						</div>
					</div>

					{/* Time Window */}
					<div className="space-y-2">
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label htmlFor="start-hour">Start Hour</Label>
								<Select
									value={localSchedule.startHour.toString()}
									onValueChange={handleStartHourChange}
								>
									<SelectTrigger
										id="start-hour"
										className={timeError ? "border-destructive" : ""}
									>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{HOURS.map((hour) => (
											<SelectItem
												key={hour.value}
												value={hour.value.toString()}
											>
												{hour.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<div className="space-y-2">
								<Label htmlFor="end-hour">End Hour</Label>
								<Select
									value={localSchedule.endHour.toString()}
									onValueChange={handleEndHourChange}
								>
									<SelectTrigger
										id="end-hour"
										className={timeError ? "border-destructive" : ""}
									>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{HOURS.map((hour) => (
											<SelectItem
												key={hour.value}
												value={hour.value.toString()}
											>
												{hour.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>
						{timeError && (
							<p className="text-xs text-destructive">{timeError}</p>
						)}
					</div>

					{/* Refresh Interval */}
					<div className="space-y-3">
						<Label>Refresh Frequency</Label>
						<RadioGroup
							value={localSchedule.interval}
							onValueChange={(value) =>
								setLocalSchedule({
									...localSchedule,
									interval: value as ForecastRefreshSchedule["interval"],
								})
							}
						>
							<div className="flex items-center space-x-2">
								<RadioGroupItem value="every-6-hours" id="every-6-hours" />
								<Label
									htmlFor="every-6-hours"
									className="font-normal cursor-pointer"
								>
									Every 6 hours
								</Label>
							</div>
							<div className="flex items-center space-x-2">
								<RadioGroupItem value="every-12-hours" id="every-12-hours" />
								<Label
									htmlFor="every-12-hours"
									className="font-normal cursor-pointer"
								>
									Every 12 hours
								</Label>
							</div>
							<div className="flex items-center space-x-2">
								<RadioGroupItem value="daily" id="daily" />
								<Label htmlFor="daily" className="font-normal cursor-pointer">
									Daily
								</Label>
							</div>
						</RadioGroup>

						{/* Combined schedule summary */}
						<p className="text-xs text-muted-foreground pt-1">
							{scheduleSummary}
						</p>
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Cancel
					</Button>
					<Button onClick={handleSave}>Save Schedule</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
